import React from 'react';
import { IMPLEMENTATION_LEVELS, ORG_TYPES, POLICY_LEVELS, POWER_LEVELS, SCALE_LEVELS, STAKEHOLDER_TYPES } from '../data/lenses.js';
import { ACTIVITY_TYPOLOGY, THEME_TYPOLOGY } from '../data/taxonomies.js';
import { COUNTRIES } from '../data/taxonomy.js';
const { useState, useRef, useEffect } = React;

// ============================================================
// Static content pages: Submit only. About + Typology live in their own files.
// ============================================================

// --------------------------------------------------------------
// Logo upload — reads a file and shows a preview
export function LogoUpload() {
  const [preview, setPreview] = useState(null);
  const [name, setName] = useState('');
  const inputRef = useRef(null);
  const onFile = (file) => {
    if (!file) return;
    setName(file.name);
    const reader = new FileReader();
    reader.onload = () => setPreview(reader.result);
    reader.readAsDataURL(file);
  };
  return (
    <div className="logo-upload">
      <div className={`logo-upload-drop ${preview ? 'has-preview' : ''}`}
      onClick={() => inputRef.current?.click()}
      onDragOver={(e) => {e.preventDefault();e.currentTarget.classList.add('drag');}}
      onDragLeave={(e) => e.currentTarget.classList.remove('drag')}
      onDrop={(e) => {e.preventDefault();e.currentTarget.classList.remove('drag');onFile(e.dataTransfer.files?.[0]);}}>
        {preview ?
        <img src={preview} alt="Logo preview" className="logo-upload-preview" /> :

        <div className="logo-upload-placeholder">
            <svg viewBox="0 0 24 24" width="28" height="28" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"><path d="M4 15v3a2 2 0 002 2h12a2 2 0 002-2v-3" /><path d="M7 10l5-5 5 5" /><path d="M12 5v12" /></svg>
            <div>Click to upload or drag & drop</div>
          </div>
        }
        <input ref={inputRef} type="file" accept="image/svg+xml,image/png" onChange={(e) => onFile(e.target.files?.[0])} hidden />
      </div>
      {name &&
      <div className="logo-upload-meta">
          <span>{name}</span>
          <button type="button" className="link-btn" onClick={() => {setPreview(null);setName('');}}>Remove</button>
        </div>
      }
    </div>);

}

// Sub-category picker: each main category is a collapsible header tinted with its color;
// inside are all its sub-categories as checkboxes.
export function SubcategoryPicker({ typology, initialSelected, onChange }) {
  // initialSelected is an array of sub-category names; we map them to "Cat::Sub" keys.
  const initSet = React.useMemo(() => {
    const s = new Set();
    if (initialSelected && initialSelected.length) {
      const lookup = {};
      typology.forEach(cat => cat.sub.forEach(sub => lookup[sub.toLowerCase()] = `${cat.name}::${sub}`));
      initialSelected.forEach(name => {
        const k = lookup[(name || '').toLowerCase()];
        if (k) s.add(k);
      });
    }
    return s;
  }, [initialSelected, typology]);
  const [openCats, setOpenCats] = useState(() => {
    // open categories that have any selection
    const open = new Set();
    typology.forEach(cat => {
      if (cat.sub.some(s => initSet.has(`${cat.name}::${s}`))) open.add(cat.name);
    });
    return open;
  });
  const [checked, setChecked] = useState(() => initSet);
  // Report selected leaf values upward as canonical "Parent | Sub" strings.
  React.useEffect(() => {
    if (!onChange) return;
    const vals = [];
    typology.forEach((cat) => cat.sub.forEach((sub) => {
      if (checked.has(`${cat.name}::${sub}`)) vals.push(`${cat.name} | ${sub}`);
    }));
    onChange(vals);
  }, [checked]);
  const toggleCat = (name) => {
    const next = new Set(openCats);
    next.has(name) ? next.delete(name) : next.add(name);
    setOpenCats(next);
  };
  const toggleSub = (key) => {
    const next = new Set(checked);
    next.has(key) ? next.delete(key) : next.add(key);
    setChecked(next);
  };
  return (
    <div className="subcat-picker">
      {typology.map((cat) => {
        const open = openCats.has(cat.name);
        const sel = cat.sub.filter((s) => checked.has(`${cat.name}::${s}`)).length;
        return (
          <div key={cat.name} className={`subcat-block ${open ? 'open' : ''}`} style={{ '--cat-color': cat.color }}>
            <button type="button" className="subcat-head" onClick={() => toggleCat(cat.name)}>
              <span className="subcat-dot" style={{ background: cat.color }} />
              <span className="subcat-name">{cat.name}</span>
              <span className="subcat-count">{sel > 0 ? `${sel} selected` : `${cat.sub.length}`}</span>
              <span className="subcat-chev">{open ? '–' : '+'}</span>
            </button>
            {open &&
            <div className="subcat-body">
                {cat.sub.map((s) => {
                const key = `${cat.name}::${s}`;
                return (
                  <label key={s} className={`subcat-sub ${checked.has(key) ? 'on' : ''}`}>
                      <input type="checkbox" checked={checked.has(key)} onChange={() => toggleSub(key)} />
                      <span>{s}</span>
                    </label>);

              })}
              </div>
            }
          </div>);

      })}
    </div>);

}

// Autocomplete org picker — searches by name, shows matches, click to select.
export function OrgAutocomplete({ orgs, value, onSelect, onAddNew }) {
  const [q, setQ] = useState(value?.name || '');
  const [focus, setFocus] = useState(false);
  React.useEffect(() => { setQ(value?.name || ''); }, [value]);
  const matches = React.useMemo(() => {
    if (!q || q.length < 2) return [];
    const needle = q.toLowerCase();
    return (orgs || []).filter(o => o.name && o.name.toLowerCase().includes(needle)).slice(0, 8);
  }, [q, orgs]);
  return (
    <div className="org-autocomplete">
      <input type="text" value={q} placeholder="Start typing the name…"
        onFocus={() => setFocus(true)}
        onBlur={() => setTimeout(() => setFocus(false), 150)}
        onChange={(e) => { setQ(e.target.value); if (value) onSelect(null); }}
        required={!value} />
      {focus && matches.length > 0 && (
        <div className="org-autocomplete-menu">
          {matches.map(o => (
            <button key={o.id} type="button" className="org-autocomplete-item"
              onMouseDown={(e) => { e.preventDefault(); onSelect(o); setQ(o.name); setFocus(false); }}>
              <div className="org-ac-name">{o.name}</div>
              <div className="org-ac-meta">{o.country || '—'}{o.foundedYear ? ` · est. ${o.foundedYear}` : ''}</div>
            </button>
          ))}
        </div>
      )}
      {focus && q.length >= 2 && matches.length === 0 && !value && (
        <div className="org-autocomplete-menu">
          <div className="org-ac-nomatch">
            No published organisation matches “{q}”.
            {onAddNew && (
              <button type="button" className="inline-link" onMouseDown={(e) => { e.preventDefault(); onAddNew(); }}>
                Add it as a new organisation →
              </button>
            )}
          </div>
        </div>
      )}
    </div>);
}

// Field-by-field update editor — mirrors the Add form layout. Each field shows
// the current value as read-only; clicking "Change" on the right flips it to
// an editable input prefilled with that value.
export function FieldChange({ label, current, hint, children, alwaysOn, editing, onToggle }) {
  const on = alwaysOn || editing;
  return (
    <div className={`upd-field ${on ? 'on' : ''}`}>
      <div className="upd-field-head">
        <label>{label}</label>
        {!alwaysOn && (
          <button type="button" className="upd-change-link" onClick={() => onToggle && onToggle(!editing)}>
            {editing ? '↺ Keep current' : '✎ Change'}
          </button>
        )}
      </div>
      {hint && <div className="field-help">{hint}</div>}
      {!on ? (
        <div className="upd-current">{current || <span className="muted">— not set —</span>}</div>
      ) : (
        <div className="upd-edit">{children}</div>
      )}
    </div>
  );
}

// Parse the org's stored (read-shape) values into editable arrays.
const splitVals = (s, sep) => (s || '').split(sep).map((x) => x.trim()).filter(Boolean);
const orgToEditable = (org) => ({
  name: org.name || '',
  website: org.website || '',
  mission: org.mission || '',
  country: org.country || '',
  city: org.city || '',
  foundedYear: org.foundedYear || '',
  type: splitVals(org.type, '|'),
  activities: org.activities || [],
  themes: org.themes || [],
  stakeholders: org.stakeholders || [],
  scale: org.scale || [],
  policy: splitVals(org.policy, ';'),
  implementation: splitVals(org.implementation, ';'),
  power: splitVals(org.power, ';'),
});
const subNames = (vals) => vals.map((v) => v.split('|')[1]?.trim()).filter(Boolean);
const lensLabels = (vals, levels) => vals.map((x) => (levels.find((l) => l.value === x) || {}).label || x);

// Field-by-field update editor. Each field is read-only until the user clicks
// "Change"; flipping it marks the field as part of the proposed diff. onChange
// receives { field: newValue } for only the fields flipped to "Change".
export function UpdateFields({ org, onChange }) {
  const fmtList = (arr) => (arr && arr.length) ? arr.join(', ') : '';
  const init = orgToEditable(org);
  const [editing, setEditing] = useState(() => new Set());
  const [vals, setVals] = useState(init);

  const toggleEdit = (field) => setEditing((s) => {
    const n = new Set(s);
    n.has(field) ? n.delete(field) : n.add(field);
    return n;
  });
  const setVal = (field) => (v) => setVals((x) => ({ ...x, [field]: v }));
  const toggleArr = (field, val) => setVals((x) => ({
    ...x, [field]: x[field].includes(val) ? x[field].filter((y) => y !== val) : [...x[field], val],
  }));

  // Emit the FULL snapshot (current values + edits) plus which fields the user
  // flipped to "Change". The request row then looks like a fresh submission.
  useEffect(() => {
    if (!onChange) return;
    onChange({ org: vals, changedFields: [...editing] });
  }, [editing, vals]);

  const ed = (field) => ({ editing: editing.has(field), onToggle: () => toggleEdit(field) });
  const chips = (field, options) => (
    <div className="chip-grid">
      {options.map((o) => {
        const val = o.value || o;
        return (
          <label key={val} className="chip-check">
            <input type="checkbox" checked={vals[field].includes(val)} onChange={() => toggleArr(field, val)} />
            <span>{o.label || o}</span>
          </label>
        );
      })}
    </div>
  );

  return (
    <div className="update-fields-v2">
      <div className="field-row">
        <FieldChange label="Organisation name *" current={org.name} {...ed('name')}>
          <input type="text" value={vals.name} onChange={(e) => setVal('name')(e.target.value)} required />
        </FieldChange>
        <FieldChange label="Website *" current={org.website ? <a href={org.website} target="_blank" rel="noopener">{org.website}</a> : ''} {...ed('website')}>
          <input type="url" value={vals.website} onChange={(e) => setVal('website')(e.target.value)} placeholder="https://…" required />
        </FieldChange>
      </div>

      <FieldChange label="One-line mission *" current={org.mission} {...ed('mission')}>
        <input type="text" value={vals.mission} onChange={(e) => setVal('mission')(e.target.value)} required />
      </FieldChange>

      <div className="field-row">
        <FieldChange label="Country / Region *" current={org.country} {...ed('country')}>
          <select value={vals.country} onChange={(e) => setVal('country')(e.target.value)} required>
            <option value="" disabled>Select a country…</option>
            {COUNTRIES.map((c) => <option key={c} value={c}>{c}</option>)}
          </select>
        </FieldChange>
        <FieldChange label="City" current={org.city || ''} {...ed('city')}>
          <input type="text" value={vals.city} onChange={(e) => setVal('city')(e.target.value)} />
        </FieldChange>
        <FieldChange label="Year founded *" current={org.foundedYear} {...ed('foundedYear')}>
          <input type="number" min="1800" max="2026" value={vals.foundedYear} onChange={(e) => setVal('foundedYear')(e.target.value)} required />
        </FieldChange>
      </div>

      <FieldChange label="Non/profit categorisation *" current={org.type} {...ed('type')}>
        {chips('type', ORG_TYPES)}
      </FieldChange>

      <div className="form-section-h">Typology — what you do</div>
      <p className="form-section-blurb">Update the activities or themes that describe the organisation today.</p>

      <FieldChange label="Activities" current={fmtList(org.activities)} {...ed('activities')}>
        <SubcategoryPicker typology={ACTIVITY_TYPOLOGY} initialSelected={subNames(org.activities || [])} onChange={setVal('activities')} />
      </FieldChange>

      <FieldChange label="Themes" current={fmtList(org.themes)} {...ed('themes')}>
        <SubcategoryPicker typology={THEME_TYPOLOGY} initialSelected={subNames(org.themes || [])} onChange={setVal('themes')} />
      </FieldChange>

      <div className="form-section-h">Lenses — how you position</div>
      <p className="form-section-blurb">These lenses locate the organisation in relation to stakeholders, geography, power, and policy.</p>

      <FieldChange label="Stakeholders engaged" current={fmtList(org.stakeholders)} {...ed('stakeholders')}>
        <div className="chip-grid chip-grid-3">
          {STAKEHOLDER_TYPES.map((s) => (
            <label key={s} className="chip-check">
              <input type="checkbox" checked={vals.stakeholders.includes(s)} onChange={() => toggleArr('stakeholders', s)} />
              <span>{s}</span>
            </label>
          ))}
        </div>
      </FieldChange>

      <FieldChange label="Geographic scale of action" current={fmtList(org.scale)} {...ed('scale')}
        hint="Pick one or more scales. Organisations often work across several.">
        {chips('scale', SCALE_LEVELS)}
      </FieldChange>

      <div className="field-row field-row-2">
        <FieldChange label="Policy proximity" current={fmtList(lensLabels(splitVals(org.policy, ';'), POLICY_LEVELS))} {...ed('policy')}>
          {chips('policy', POLICY_LEVELS)}
        </FieldChange>
        <FieldChange label="Implementation proximity" current={fmtList(lensLabels(splitVals(org.implementation, ';'), IMPLEMENTATION_LEVELS))} {...ed('implementation')}>
          {chips('implementation', IMPLEMENTATION_LEVELS)}
        </FieldChange>
      </div>

      <FieldChange label="Power" current={fmtList(lensLabels(splitVals(org.power, ';'), POWER_LEVELS))} {...ed('power')}>
        {chips('power', POWER_LEVELS)}
      </FieldChange>
    </div>
  );
}

const EMPTY_FORM = {
  name: '', website: '', mission: '', country: '', city: '', foundedYear: '',
  type: [], activities: [], themes: [], scale: [], stakeholders: [],
  policy: [], implementation: [], power: [],
  contactName: '', contactEmail: '', role: '', consent: false,
  website_confirm: '', // honeypot
};

// --- Client-side validation ---------------------------------------------------
// Mirrors the server rules in netlify/functions/submit-organization.mjs so users
// get immediate, specific feedback and a valid payload before any round-trip.
const EMAIL_RE = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
const isHttpUrl = (v) => /^https?:\/\/\S+/i.test((v || '').trim());

// Friendly labels for the server's `fields` error strings ("key: message").
const SERVER_FIELD_LABELS = {
  name: 'Organisation name', website: 'Website', mission: 'Mission',
  country: 'Country', city: 'City', foundedYear: 'Year founded',
  type: 'Non/profit categorisation', activities: 'Activities', themes: 'Themes',
  scale: 'Geographic scale', stakeholders: 'Stakeholders',
  policy: 'Policy proximity', implementation: 'Implementation proximity', power: 'Power',
  contactName: 'Your name', contactEmail: 'Your email', role: 'Your role',
  orgId: 'Organisation', changedFields: 'Changed fields',
  'requester.name': 'Your name', 'requester.email': 'Your email',
};

// Turn a raw server error ("activities: required") into a friendly sentence.
function prettyServerError(raw) {
  const i = String(raw).indexOf(':');
  if (i === -1) return String(raw);
  const key = raw.slice(0, i).trim();
  const msg = raw.slice(i + 1).trim();
  const label = SERVER_FIELD_LABELS[key] || key;
  if (/^required$/i.test(msg)) return `${label}: please fill this in.`;
  return `${label}: ${msg}`;
}

function validateAdd(f) {
  const e = [];
  if (!f.name.trim()) e.push('Organisation name: please fill this in.');
  if (!f.website.trim()) e.push('Website: please fill this in.');
  else if (!isHttpUrl(f.website)) e.push('Website: enter a full URL starting with http:// or https://.');
  if (!f.mission.trim()) e.push('Mission: please add a short description.');
  else if (f.mission.trim().length > 300) e.push('Mission: keep it under 300 characters.');
  if (!f.country) e.push('Country: please choose one from the list.');
  const yr = Number(f.foundedYear);
  if (!String(f.foundedYear).trim()) e.push('Year founded: please fill this in.');
  else if (!Number.isInteger(yr) || yr < 1800 || yr > 2026) e.push('Year founded: enter a year between 1800 and 2026.');
  if (f.type.length === 0) e.push('Non/profit categorisation: select at least one option.');
  if (f.activities.length === 0) e.push('Activities: select at least one activity.');
  if (f.themes.length === 0) e.push('Themes: select at least one theme.');
  if (!f.contactName.trim()) e.push('Your name: please fill this in.');
  if (!f.contactEmail.trim()) e.push('Your email: please fill this in.');
  else if (!EMAIL_RE.test(f.contactEmail.trim())) e.push('Your email: enter a valid email address.');
  if (!f.consent) e.push('Consent: please confirm you are authorised and agree to the licence.');
  return e;
}

function validateUpdate(f, selectedOrg, updateData) {
  const e = [];
  if (!selectedOrg) e.push('Organisation: search for and select the organisation you want to update.');
  else if (!updateData.changedFields || updateData.changedFields.length === 0)
    e.push('Changed fields: click “Change” on at least one field and edit it.');
  if (!f.contactName.trim()) e.push('Your name: please fill this in.');
  if (!f.contactEmail.trim()) e.push('Your email: please fill this in.');
  else if (!EMAIL_RE.test(f.contactEmail.trim())) e.push('Your email: enter a valid email address.');
  return e;
}

export function SubmitPage({ onNavigate, orgs }) {
  const [mode, setMode] = useState('add'); // 'add' | 'update'
  const [submitted, setSubmitted] = useState(false);
  const [submitError, setSubmitError] = useState(false);
  const [selectedOrg, setSelectedOrg] = useState(null);
  const [form, setForm] = useState(EMPTY_FORM);
  const [updateData, setUpdateData] = useState({ org: {}, changedFields: [] }); // full snapshot + which changed
  const [busy, setBusy] = useState(false);
  const [errors, setErrors] = useState([]); // friendly validation messages shown inline
  const errorRef = useRef(null);

  const showErrors = (list) => {
    setErrors(list);
    // Let the summary render, then bring it into view.
    requestAnimationFrame(() => errorRef.current?.scrollIntoView({ behavior: 'smooth', block: 'center' }));
  };

  // Submitting is an in-page state flip (not a route change), so the route's
  // scroll-to-top never fires — reset scroll when the confirmation renders.
  useEffect(() => {
    if (submitted || submitError) window.scrollTo(0, 0);
  }, [submitted, submitError]);

  const setField = (k) => (v) => setForm((f) => ({ ...f, [k]: v }));
  const toggleIn = (k, val) => setForm((f) => ({
    ...f, [k]: f[k].includes(val) ? f[k].filter((x) => x !== val) : [...f[k], val],
  }));

  async function postSubmission(payload) {
    setBusy(true);
    try {
      const res = await fetch('/.netlify/functions/submit-organization', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(payload),
      });
      if (res.ok) { setErrors([]); setSubmitted(true); return; }
      // Try to surface the server's specific field errors (422) inline.
      let body = null;
      try { body = await res.json(); } catch { /* non-JSON error */ }
      if (res.status === 422 && body && Array.isArray(body.fields) && body.fields.length) {
        showErrors(body.fields.map(prettyServerError));
      } else {
        // 5xx / network-shaped / unexpected: fall back to the full-page notice.
        setSubmitError(true);
      }
    } catch {
      setSubmitError(true);
    } finally {
      setBusy(false);
    }
  }

  function handleSubmit(e) {
    e.preventDefault();
    // Validate on the client first so problems are specific and instant.
    const clientErrors = mode === 'add'
      ? validateAdd(form)
      : validateUpdate(form, selectedOrg, updateData);
    if (clientErrors.length) { showErrors(clientErrors); return; }
    setErrors([]);

    if (mode === 'add') {
      postSubmission({ ...form, mode: 'create', foundedYear: Number(form.foundedYear) });
    } else {
      const org = { ...updateData.org, foundedYear: Number(updateData.org.foundedYear) || updateData.org.foundedYear };
      postSubmission({
        mode: 'update',
        orgId: selectedOrg.id,
        orgName: selectedOrg.name,
        changedFields: updateData.changedFields,
        requester: { name: form.contactName, email: form.contactEmail },
        org,
      });
    }
  }

  if (submitError) {
    return (
      <div className="static-page">
        <section className="static-hero">
          <div className="static-hero-inner" style={{ textAlign: 'center' }}>
            <div className="success-check error">
              <svg viewBox="0 0 48 48" width="48" height="48"><circle cx="24" cy="24" r="22" fill="none" stroke="currentColor" strokeWidth="2" /><path d="M17 17l14 14M31 17L17 31" fill="none" stroke="currentColor" strokeWidth="3" strokeLinecap="round" /></svg>
            </div>
            <h1 className="static-title">Something went wrong.</h1>
            <p className="static-lede" style={{ marginInline: 'auto' }}>
              We couldn't submit your entry just now. Your answers are still here —
              try again, or email us at <a className="inline-link" href="mailto:NEindexEU@metabolic.nl">NEindexEU@metabolic.nl</a>.
            </p>
            <div style={{ marginTop: 24, display: 'flex', gap: 12, justifyContent: 'center' }}>
              <button className="btn-primary" onClick={() => setSubmitError(false)}>Try again</button>
              <a className="btn-secondary" href="#/" onClick={(e) => {e.preventDefault();onNavigate('directory');}}>Back to directory</a>
            </div>
          </div>
        </section>
      </div>);
  }

  if (submitted) {
    return (
      <div className="static-page">
        <section className="static-hero">
          <div className="static-hero-inner" style={{ textAlign: 'center' }}>
            <div className="success-check">
              <svg viewBox="0 0 48 48" width="48" height="48"><circle cx="24" cy="24" r="22" fill="none" stroke="currentColor" strokeWidth="2" /><path d="M14 24l7 7 14-14" fill="none" stroke="currentColor" strokeWidth="3" strokeLinecap="round" strokeLinejoin="round" /></svg>
            </div>
            <h1 className="static-title">Thank you.</h1>
            <p className="static-lede" style={{ marginInline: 'auto' }}>
              Your submission has been received. A steward will review it within
              two weeks and get in touch if we need anything further.
            </p>
            <div style={{ marginTop: 24, display: 'flex', gap: 12, justifyContent: 'center' }}>
              <a className="btn-primary" href="#/" onClick={(e) => {e.preventDefault();onNavigate('directory');}}>Back to directory</a>
              <button className="btn-secondary" onClick={() => setSubmitted(false)}>Submit another</button>
            </div>
          </div>
        </section>
      </div>);

  }

  return (
    <div className="static-page">
      <section className="static-hero">
        <div className="static-hero-inner">
          <div className="static-eyebrow">Contribute</div>
          <h1 className="static-title">Help us map<br />the ecosystem.</h1>
          <p className="static-lede">The Index is only as good as the movement that shapes it. Add your organisation, or let us know if something needs updating.


          </p>
        </div>
      </section>

      <section className="static-section">
        <div className="static-col-full">
          <div className="mode-switch">
            <button className={mode === 'add' ? 'on' : ''} onClick={() => { setMode('add'); setErrors([]); }}>
              <span className="mode-icon">＋</span>
              <div>
                <div className="mode-title">Add an organisation</div>
                <div className="mode-sub">Put your work on the map</div>
              </div>
            </button>
            <button className={mode === 'update' ? 'on' : ''} onClick={() => { setMode('update'); setErrors([]); }}>
              <span className="mode-icon">✎</span>
              <div>
                <div className="mode-title">Request an update</div>
                <div className="mode-sub">Fix or refresh an existing entry</div>
              </div>
            </button>
          </div>

          <form className="submit-form" onSubmit={handleSubmit} noValidate>
            {errors.length > 0 &&
            <div className="form-errors" role="alert" ref={errorRef}>
                <div className="form-errors-head">
                  <svg viewBox="0 0 20 20" width="18" height="18" aria-hidden="true"><circle cx="10" cy="10" r="9" fill="none" stroke="currentColor" strokeWidth="1.6" /><path d="M10 5.5v5.5" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" /><circle cx="10" cy="14.2" r="1.1" fill="currentColor" /></svg>
                  <span>Please fix {errors.length === 1 ? 'this' : `these ${errors.length}`} before submitting:</span>
                </div>
                <ul>{errors.map((er, i) => <li key={i}>{er}</li>)}</ul>
              </div>
            }
            {mode === 'update' &&
            <div className="field-row">
                <div className="field">
                  <label>Which organisation? *</label>
                  <OrgAutocomplete orgs={orgs} value={selectedOrg} onSelect={setSelectedOrg}
                    onAddNew={() => { setSelectedOrg(null); setMode('add'); }} />
                  <div className="field-help">Pick your organisation from the list. Not listed? You can add it instead.</div>
                </div>
              </div>
            }

            <div className="form-section-h">{mode === 'add' ? 'About the organisation' : 'What needs to change?'}</div>
            {mode === 'update' && selectedOrg && <p className="form-section-blurb">Each field shows the current listing. Click <strong>Change</strong> on any field to propose an update.</p>}

            {mode === 'add' ?
            <>
                <div className="field-row">
                  <div className="field">
                    <label>Organisation name *</label>
                    <input type="text" required placeholder="e.g. Wellbeing Economy Alliance"
                      value={form.name} onChange={(e) => setField('name')(e.target.value)} />
                  </div>
                  <div className="field">
                    <label>Website *</label>
                    <input type="url" required placeholder="https://…"
                      value={form.website} onChange={(e) => setField('website')(e.target.value)} />
                  </div>
                </div>

                <div className="field">
                  <label>Your organisation's mission *</label>
                  <textarea rows="3" required maxLength={300} placeholder="Include a few sentences describing your goals, theory of change, and approach."
                    value={form.mission} onChange={(e) => setField('mission')(e.target.value)} />
                </div>

                <div className="field-row">
                  <div className="field">
                    <label>Country / Region *</label>
                    <select required value={form.country} onChange={(e) => setField('country')(e.target.value)}>
                      <option value="" disabled>Select a country…</option>
                      {COUNTRIES.map((c) => <option key={c} value={c}>{c}</option>)}
                    </select>
                  </div>
                  <div className="field">
                    <label>City</label>
                    <input type="text" placeholder="Optional"
                      value={form.city} onChange={(e) => setField('city')(e.target.value)} />
                  </div>
                  <div className="field">
                    <label>Year founded *</label>
                    <input type="number" min="1800" max="2026" placeholder="YYYY" required
                      value={form.foundedYear} onChange={(e) => setField('foundedYear')(e.target.value)} />
                  </div>
                </div>

                <div className="field">
                  <label>Non/profit categorisation * <span className="field-help-inline">(select all that apply)</span></label>
                  <div className="chip-grid">
                    {ORG_TYPES.map((t) =>
                      <label key={t} className="chip-check"><input type="checkbox" checked={form.type.includes(t)} onChange={() => toggleIn('type', t)} /><span>{t}</span></label>
                    )}
                  </div>
                </div>

                <div className="form-section-h">Typology — what you do</div>
                <p className="form-section-blurb">Expand a category to select the specific sub-categories that apply. You can pick sub-categories from more than one.</p>
                <div className="field">
                  <label>Activities *</label>
                  <SubcategoryPicker typology={ACTIVITY_TYPOLOGY} onChange={setField('activities')} />
                </div>

                <div className="field">
                  <label>Themes *</label>
                  <SubcategoryPicker typology={THEME_TYPOLOGY} onChange={setField('themes')} />
                </div>

                <div className="form-section-h">Lenses — how you position</div>
                <p className="form-section-blurb">These lenses locate the organisation in relation to stakeholders, geography, power, and policy. Select all that apply.</p>

                <div className="field">
                  <label>Stakeholders engaged</label>
                  <div className="chip-grid chip-grid-3">
                    {STAKEHOLDER_TYPES.map((s) =>
                  <label key={s} className="chip-check"><input type="checkbox" checked={form.stakeholders.includes(s)} onChange={() => toggleIn('stakeholders', s)} /><span>{s}</span></label>
                  )}
                  </div>
                </div>

                <div className="field">
                  <label>Geographic scale of action</label>
                  <div className="chip-grid">
                    {SCALE_LEVELS.map((s) =>
                  <label key={s.value} className="chip-check"><input type="checkbox" checked={form.scale.includes(s.value)} onChange={() => toggleIn('scale', s.value)} /><span>{s.label}</span></label>
                  )}
                  </div>
                  <div className="field-help">Pick one or more scales. Organisations often work across several.</div>
                </div>

                <div className="field-row field-row-2">
                  <div className="field">
                    <label>Policy proximity</label>
                    <div className="chip-grid">
                      {POLICY_LEVELS.map((s) =>
                    <label key={s.value} className="chip-check"><input type="checkbox" checked={form.policy.includes(s.value)} onChange={() => toggleIn('policy', s.value)} /><span>{s.label}</span></label>
                    )}
                    </div>
                  </div>
                  <div className="field">
                    <label>Implementation proximity</label>
                    <div className="chip-grid">
                      {IMPLEMENTATION_LEVELS.map((s) =>
                    <label key={s.value} className="chip-check"><input type="checkbox" checked={form.implementation.includes(s.value)} onChange={() => toggleIn('implementation', s.value)} /><span>{s.label}</span></label>
                    )}
                    </div>
                  </div>
                </div>

                <div className="field">
                  <label>Power</label>
                  <div className="chip-grid">
                    {POWER_LEVELS.map((s) =>
                  <label key={s.value} className="chip-check"><input type="checkbox" checked={form.power.includes(s.value)} onChange={() => toggleIn('power', s.value)} /><span>{s.label}</span></label>
                  )}
                  </div>
                </div>
              </> :

            <div className="field">
                {selectedOrg ? (
                  <UpdateFields key={selectedOrg.id} org={selectedOrg} onChange={setUpdateData} />
                ) : (
                  <div className="update-empty muted">Search for an organisation above to see its current listing and choose what to update.</div>
                )}
              </div>
            }

            <div className="form-section-h">About you</div>
            <div className="field-row">
              <div className="field">
                <label>Your name *</label>
                <input type="text" required value={form.contactName} onChange={(e) => setField('contactName')(e.target.value)} />
              </div>
              <div className="field">
                <label>Your email *</label>
                <input type="email" required value={form.contactEmail} onChange={(e) => setField('contactEmail')(e.target.value)} />
              </div>
              <div className="field">
                <label>Your role</label>
                <input type="text" placeholder="e.g. Director, Trustee, Member" value={form.role} onChange={(e) => setField('role')(e.target.value)} />
              </div>
            </div>

            {/* Honeypot: hidden from users; bots that fill it are silently dropped server-side. */}
            <input type="text" tabIndex={-1} autoComplete="off" aria-hidden="true"
              style={{ position: 'absolute', left: '-9999px', width: 1, height: 1, opacity: 0 }}
              value={form.website_confirm} onChange={(e) => setField('website_confirm')(e.target.value)} />

            <label className="consent-row">
              <input type="checkbox" required checked={form.consent} onChange={(e) => setField('consent')(e.target.checked)} />
              <span>I confirm I'm authorised to submit this information on behalf of the organisation, and I agree to it being published under the Index's Creative Commons licence.</span>
            </label>

            <div className="form-actions">
              <button type="submit" className="btn-primary btn-lg" disabled={busy}>
                {busy ? 'Submitting…' : (mode === 'add' ? 'Submit organisation' : 'Submit change request')}
              </button>
              <span className="form-note">Someone will review and process your request soon!</span>
            </div>
          </form>
        </div>
      </section>
    </div>);

}
